import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { lazy, Suspense } from "react";
import { Navigate } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AppProvider } from "@/lib/onboardingContext";
import { AuthProvider } from "@/lib/authContext";
import { SubscriptionProvider } from "@/lib/subscriptionContext";
import { I18nProvider } from "@/lib/i18nContext";
import { LocationProvider } from "@/lib/locationContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import FeatureGate from "@/components/FeatureGate";
import Landing from "./pages/Landing";
// Wrap dynamic imports so that a stale chunk (after a new deploy) triggers a
// one-time hard reload instead of leaving the user on a blank screen with a
// "Importing a module script failed" TypeError.
function lazyWithReload<T extends { default: React.ComponentType<any> }>(
  factory: () => Promise<T>,
) {
  return lazy(async () => {
    try {
      return await factory();
    } catch (err: any) {
      const msg = String(err?.message || err || "");
      const isChunkError =
        msg.includes("Importing a module script failed") ||
        msg.includes("Failed to fetch dynamically imported module") ||
        msg.includes("error loading dynamically imported module") ||
        msg.includes("Unable to preload CSS");
      if (isChunkError && typeof window !== "undefined") {
        const key = "__oplif_chunk_reload__";
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, "1");
          window.location.reload();
          // Return a never-resolving promise so Suspense keeps the fallback
          // visible until the reload completes.
          return new Promise<T>(() => {}) as unknown as T;
        }
      }
      throw err;
    }
  });
}
const lazy2 = lazyWithReload;
const Auth = lazy2(() => import("./pages/Auth"));
const Onboarding = lazy2(() => import("./pages/Onboarding"));
const Dashboard = lazy2(() => import("./pages/Dashboard"));
const Locations = lazy2(() => import("./pages/Locations"));
const MyLocations = lazy2(() => import("./pages/MyLocations"));
const Timeline = lazy2(() => import("./pages/Timeline"));
const Permits = lazy2(() => import("./pages/Permits"));
const BudgetPage = lazy2(() => import("./pages/Budget"));
const Checklist = lazy2(() => import("./pages/Checklist"));
const Assistant = lazy2(() => import("./pages/Assistant"));
const Pricing = lazy2(() => import("./pages/Pricing"));
const Billing = lazy2(() => import("./pages/Billing"));
const Enterprise = lazy2(() => import("./pages/Enterprise"));
const Forecast = lazy2(() => import("./pages/Forecast"));
const CashFlowForecast = lazy2(() => import("./pages/CashFlowForecast"));
const InvoiceTracker = lazy2(() => import("./pages/InvoiceTracker"));
const BreakEvenCalculator = lazy2(() => import("./pages/BreakEvenCalculator"));
const WasteTracker = lazy2(() => import("./pages/WasteTracker"));
const FoodCostByItem = lazy2(() => import("./pages/FoodCostByItem"));
const SupplierManager = lazy2(() => import("./pages/SupplierManager"));
const TrainingTracker = lazy2(() => import("./pages/TrainingTracker"));
const TipDistribution = lazy2(() => import("./pages/TipDistribution"));
const TemperatureLog = lazy2(() => import("./pages/TemperatureLog"));
const OperationsModule = lazy2(() => import("./pages/OperationsModule"));
const PlanPreview = lazy2(() => import("./pages/PlanPreview"));
const Affiliates = lazy2(() => import("./pages/Affiliates"));
const SharedRoadmap = lazy2(() => import("./pages/SharedRoadmap"));
const CostCalculator = lazy2(() => import("./pages/CostCalculator"));
const AdminListings = lazy2(() => import("./pages/AdminListings"));
const Equipment = lazy2(() => import("./pages/Equipment"));
const Inventory = lazy2(() => import("./pages/Inventory"));
const StaffRota = lazy2(() => import("./pages/StaffRota"));
const Operations = lazy2(() => import("./pages/Operations"));
const OperationsDashboard = lazy2(() => import("./pages/OperationsDashboard"));
const Demo = lazy2(() => import("./pages/Demo"));
const Features = lazy2(() => import("./pages/Features"));
const Contact = lazy2(() => import("./pages/Contact"));
const Press = lazy2(() => import("./pages/Press"));
const Checkout = lazy2(() => import("./pages/Checkout"));
const Unsubscribe = lazy2(() => import("./pages/Unsubscribe"));
const SupportedCities = lazy2(() => import("./pages/SupportedCities"));
const Admin = lazy2(() => import("./pages/Admin"));
const Terms = lazy2(() => import("./pages/Terms"));
const StripeFlowChecklist = lazy2(() => import("./pages/StripeFlowChecklist"));
const SettingsIntegrations = lazy2(() => import("./pages/SettingsIntegrations"));
const Settings = lazy2(() => import("./pages/Settings"));
const SquareCallback = lazy2(() => import("./pages/SquareCallback"));
const OurStory = lazy2(() => import("./pages/OurStory"));
const Mission = lazy2(() => import("./pages/Mission"));
const Benchmarks = lazy2(() => import("./pages/Benchmarks"));
const SharedBriefing = lazy2(() => import("./pages/SharedBriefing"));
const Blog = lazy2(() => import("./pages/Blog"));
const BlogPost = lazy2(() => import("./pages/BlogPost"));
const NotFound = lazy2(() => import("./pages/NotFound"));
const FreePreshiftGenerator = lazy2(() => import("./pages/FreePreshiftGenerator"));
const GroupOverview = lazy2(() => import("./pages/GroupOverview"));

const queryClient = new QueryClient();

function AppRoutes() {
  const location = useLocation();
  const normalizedPath = location.pathname.replace(/\/+$/, "") || "/";

  if (normalizedPath === "/demo") {
    return (
      <Suspense fallback={null}>
        <Routes>
          <Route path="/demo" element={<Demo />} />
        </Routes>
      </Suspense>
    );
  }

  return (
    <AuthProvider>
      <SubscriptionProvider>
        <LocationProvider>
          <AppProvider>
            <Suspense fallback={null}>
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/features" element={<Features />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/press" element={<Press />} />
                <Route path="/enterprise" element={<Enterprise />} />
                <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
                <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                <Route path="/locations" element={<ProtectedRoute><FeatureGate feature="locations.unlimited" featureName="Location browser"><Locations /></FeatureGate></ProtectedRoute>} />
                <Route path="/my-locations" element={<ProtectedRoute><FeatureGate feature="multiLocation" featureName="My Locations"><MyLocations /></FeatureGate></ProtectedRoute>} />
                <Route path="/timeline" element={<ProtectedRoute><FeatureGate feature="openingPlan" featureName="Opening Timeline"><Timeline /></FeatureGate></ProtectedRoute>} />
                <Route path="/permits" element={<ProtectedRoute><Permits /></ProtectedRoute>} />
                <Route path="/budget" element={<ProtectedRoute><FeatureGate feature="budget" featureName="Budget Planner"><BudgetPage /></FeatureGate></ProtectedRoute>} />
                <Route path="/checklist" element={<ProtectedRoute><Checklist /></ProtectedRoute>} />
                <Route path="/assistant" element={<ProtectedRoute><FeatureGate feature="aiAdvisor" featureName="AI Business Advisor"><Assistant /></FeatureGate></ProtectedRoute>} />
                <Route path="/forecast" element={<ProtectedRoute><FeatureGate feature="forecast" featureName="Revenue Forecast"><Forecast /></FeatureGate></ProtectedRoute>} />
                <Route path="/cash-flow" element={<ProtectedRoute><CashFlowForecast /></ProtectedRoute>} />
                <Route path="/invoices" element={<ProtectedRoute><InvoiceTracker /></ProtectedRoute>} />
                <Route path="/break-even" element={<ProtectedRoute><BreakEvenCalculator /></ProtectedRoute>} />
                <Route path="/waste-tracker" element={<ProtectedRoute><WasteTracker /></ProtectedRoute>} />
                <Route path="/food-cost" element={<ProtectedRoute><FeatureGate feature="foodCost" featureName="Food Cost by Item"><FoodCostByItem /></FeatureGate></ProtectedRoute>} />
                <Route path="/suppliers" element={<ProtectedRoute><SupplierManager /></ProtectedRoute>} />
                <Route path="/training" element={<ProtectedRoute><TrainingTracker /></ProtectedRoute>} />
                <Route path="/tips" element={<ProtectedRoute><TipDistribution /></ProtectedRoute>} />
                <Route path="/temperature" element={<ProtectedRoute><TemperatureLog /></ProtectedRoute>} />
                <Route path="/plan" element={<ProtectedRoute><PlanPreview /></ProtectedRoute>} />
                <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
                <Route path="/billing" element={<Navigate to="/settings?section=billing" replace />} />
                <Route path="/affiliates" element={<Affiliates />} />
                <Route path="/shared" element={<SharedRoadmap />} />
                <Route path="/calculator" element={<ProtectedRoute><CostCalculator /></ProtectedRoute>} />
                <Route path="/admin/listings" element={<ProtectedRoute><AdminListings /></ProtectedRoute>} />
                <Route path="/equipment" element={<ProtectedRoute><FeatureGate feature="equipment" featureName="Equipment Planner"><Equipment /></FeatureGate></ProtectedRoute>} />
                <Route path="/inventory" element={<ProtectedRoute><FeatureGate feature="inventory" featureName="Inventory"><Inventory /></FeatureGate></ProtectedRoute>} />
                <Route path="/staff-rota" element={<ProtectedRoute><FeatureGate feature="staffRota" featureName="Staff Rota"><StaffRota /></FeatureGate></ProtectedRoute>} />
                <Route path="/operations" element={<ProtectedRoute><FeatureGate feature="operations" featureName="Operations"><Operations /></FeatureGate></ProtectedRoute>} />
                <Route path="/dashboard/operations" element={<ProtectedRoute><OperationsDashboard /></ProtectedRoute>} />
                <Route path="/dashboard/operations/:slug" element={<ProtectedRoute><OperationsModule /></ProtectedRoute>} />
                <Route path="/group-overview" element={<ProtectedRoute><GroupOverview /></ProtectedRoute>} />
                <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
                <Route path="/unsubscribe" element={<Unsubscribe />} />
                <Route path="/supported-cities" element={<SupportedCities />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/our-story" element={<OurStory />} />
                <Route path="/mission" element={<Mission />} />
                <Route path="/benchmarks" element={<Benchmarks />} />
                <Route path="/briefing/:shareId" element={<SharedBriefing />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/free-preshift-generator" element={<FreePreshiftGenerator />} />
                <Route path="/stripe-flow-checklist" element={<ProtectedRoute><StripeFlowChecklist /></ProtectedRoute>} />
                <Route path="/settings/integrations" element={<Navigate to="/settings?section=integrations" replace />} />
                <Route path="/auth/square/callback" element={<SquareCallback />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </AppProvider>
        </LocationProvider>
      </SubscriptionProvider>
    </AuthProvider>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <I18nProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </I18nProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
